/* eslint no-console: 0 */
import LinearProgress from '@material-ui/core/LinearProgress';
import Tooltip from '@material-ui/core/Tooltip';
import { Alert } from '@mui/material';
import React, { Component } from 'react';

import PushService from '../../../services/PushService';
import Button from '../../bits/Button.jsx';
import Switch from '../../bits/Switch.jsx';
import Tile from '../../bits/Tile.jsx';
import TileActionRow from '../../bits/TileActionRow.jsx';
import Loading from '../Loading.jsx';

import { customFetchV2 } from '../../../services/custom-fetch-v2.ts';

/** Renders notification guidance with the shared compact blue info treatment. */
function SmsInfoAlert({ children }) {
  return (
    <Alert
      severity="info"
      sx={{
        alignItems: 'center',
        backgroundColor: 'transparent',
        border: 0,
        color: '#52616e',
        fontSize: '0.875rem',
        lineHeight: 1.4,
        padding: '2px 0',
        '& .MuiAlert-icon': {
          color: '#4a8bcc',
          fontSize: '20px',
          marginRight: '8px',
          opacity: 1,
          padding: 0,
        },
        '& .MuiAlert-message': {
          padding: 0,
        },
      }}>
      {children}
    </Alert>
  );
}

/** Explains whether SMS can be enabled and which devices can deliver it. */
function SmsNotificationDetails({
  available, eligible, enabled,
}) {
  if (!eligible) {
    return (
      <div style={{ marginTop: 6 }}>
        <SmsInfoAlert>
          SMS notifications can only be enabled if you have devices on the Unlimited plan.
        </SmsInfoAlert>
      </div>
    );
  }

  if (!available) {
    return (
      <div style={{ marginTop: 6 }}>
        <SmsInfoAlert>
          You must verify your phone number in Settings before you can enable SMS alerts.
        </SmsInfoAlert>
      </div>
    );
  }

  if (!enabled) {
    return null;
  }

  return (
    <div style={{ marginTop: 6 }}>
      <SmsInfoAlert>
        SMS alerts are only sent for devices on Unlimited plans.
      </SmsInfoAlert>
    </div>
  );
}

/** Displays current SMS usage across the full width of its settings row. */
function SmsNotificationUsage({ used, quota }) {
  if (quota <= 0) {
    return null;
  }

  const percentageUsed = Math.min(100, Math.max(0, (used / quota) * 100));

  return (
    <div className="sms-notification-usage" style={{ marginTop: 8 }}>
      <div>{used} of {quota} SMS alerts used this month</div>
      <LinearProgress
        aria-label="SMS alert quota usage"
        className="sms-notification-usage__progress"
        style={{ backgroundColor: '#d9dee3', marginTop: 6 }}
        variant="determinate"
        value={percentageUsed}
      />
    </div>
  );
}

/** Collects explicit consent before the existing SMS preference is enabled. */
function SmsConsentPanel({
  accepted, error, loading, onAcceptedChange, onCancel, onConfirm,
}) {
  return (
    <div id="sms-consent-panel" className="sms-consent-panel">
      <div className="sms-consent-panel__title">Enable SMS alerts</div>
      <label className="sms-consent-panel__agreement">
        <input
          type="checkbox"
          name="smsConsent"
          checked={accepted}
          disabled={loading}
          onChange={onAcceptedChange}
        />
        <span>
          I agree to receive recurring automated SMS alerts from ShieldGPS about GPS tracker
          events I have enabled.
        </span>
      </label>
      <div className="sms-consent-panel__disclosure">
        Message frequency varies. Message and data rates may apply. Reply HELP for help or STOP
        to cancel. Consent is not a condition of purchase.
      </div>
      <div className="sms-consent-panel__links">
        <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
          Terms of Service
        </a>
        <span aria-hidden="true"> · </span>
        <a href="/privacy" target="_blank" rel="noopener noreferrer">
          Privacy Policy
        </a>
      </div>
      {error ? <div role="alert" className="sms-consent-panel__error">{error}</div> : null}
      <div className="sms-consent-panel__actions">
        <Button
          color="secondary"
          disabled={loading}
          onClick={onCancel}>Cancel</Button>
        <Button
          color="primary"
          disabled={!accepted || loading}
          loading={loading}
          onClick={onConfirm}>Enable SMS alerts</Button>
      </div>
    </div>
  );
}

class NotificationsRoute extends Component {
  state = {
    isLoading: true,
    pushNotifications: false,
    pushNotificationsDisabled: true,
    emailNotifications: false,
    smsNotifications: false,
    smsNotificationsEligible: false,
    smsNotificationsAvailable: false,
    smsMonthlyQuota: 0,
    smsMonthlyUsed: 0,
    smsConsentExpanded: false,
    smsConsentAccepted: false,
    smsConsentError: null,
    emailReceipts: false,
    pendingConfigUpdates: {},
  };

  componentDidMount() {
    this.pushService = new PushService();
    window.pushService = this.pushService;

    this.loadUserConfig();

    // Set initial state of pushNotifications switch based on local client state
    if (!this.pushService.isPushSupported() || this.pushService.getPushPermission() === 'denied') {
      // Leave in default state
      this.setState({
        pushNotifications: false,
        pushNotificationsDisabled: true,
      });
    } else {
      this.pushService.isPushEnabled().then((isEnabled) => {
        this.setState({
          pushNotifications: isEnabled,
          pushNotificationsDisabled: false,
        });
      });
    }
  }

  loadUserConfig = () => {
    this.setState({
      isLoading: true,
    });

    customFetchV2('/sessions/me', {
      method: 'GET',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Ooops');
        }

        return response.data;
      })
      .then((json) => {
        this.setState({
          isLoading: false,
          emailNotifications: json.config.emailNotifications,
          smsNotifications: json.config.smsNotifications,
          smsNotificationsEligible: json.config.smsNotificationsEligible,
          smsNotificationsAvailable: json.config.smsNotificationsAvailable,
          smsMonthlyQuota: json.config.smsMonthlyQuota,
          smsMonthlyUsed: json.config.smsMonthlyUsed,
          smsConsentExpanded: false,
          smsConsentAccepted: false,
          smsConsentError: null,
          emailReceipts: json.config.emailReceipts,
        });
      });
  };

  /** Persists a notification preference before reflecting it in local state. */
  updateConfig = (name, checked, { onFailure, onSuccess } = {}) => {
    this.setState(({ pendingConfigUpdates }) => ({
      pendingConfigUpdates: {
        ...pendingConfigUpdates,
        [name]: true,
      },
    }));

    customFetchV2('/api/v2/users/me/config', {
      method: 'PATCH',
      body: JSON.stringify({
        [name]: checked,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Ooops');
        }
        this.setState(({ pendingConfigUpdates }) => ({
          [name]: checked,
          pendingConfigUpdates: {
            ...pendingConfigUpdates,
            [name]: false,
          },
        }), onSuccess);
      })
      .catch(() => {
        this.setState(({ pendingConfigUpdates }) => ({
          pendingConfigUpdates: {
            ...pendingConfigUpdates,
            [name]: false,
          },
        }), onFailure);
      });
  }

  handleSwitchChange = (event) => {
    const { checked, name } = event.target;
    this.updateConfig(name, checked);
  }

  /** Expands consent before enabling SMS; disabling remains immediate. */
  handleSmsNotificationsChange = (event) => {
    if (event.target.checked) {
      this.setState({
        smsConsentExpanded: true,
        smsConsentAccepted: false,
        smsConsentError: null,
      });
      return;
    }

    this.updateConfig('smsNotifications', false);
  }

  handleSmsConsentAcceptedChange = (event) => {
    this.setState({
      smsConsentAccepted: event.target.checked,
      smsConsentError: null,
    });
  }

  cancelSmsConsent = () => {
    this.setState({
      smsConsentExpanded: false,
      smsConsentAccepted: false,
      smsConsentError: null,
    });
  }

  confirmSmsConsent = () => {
    if (!this.state.smsConsentAccepted) {
      return;
    }

    this.setState({ smsConsentError: null });
    this.updateConfig('smsNotifications', true, {
      onSuccess: () => {
        this.setState({
          smsConsentExpanded: false,
          smsConsentAccepted: false,
          smsConsentError: null,
        });
      },
      onFailure: () => {
        this.setState({
          smsConsentError: 'Unable to enable SMS alerts. Please try again.',
        });
      },
    });
  }

  handlePushNotificationsChange = (event) => {
    const enablePush = event.target.checked;

    // If pushOn, then we want to request permissions from the browser and then send token to server
    // If pushOff, then we want to delete subscriptions on the server
    if (enablePush) {
      console.log('Requesting push subscription...');
      this.pushService.requestPushPermission().then((result) => {
        console.log(result);
        const platform = result[0];
        const subscription = result[1];

        // Leave any exceptions uncaught
        this.saveSubscription(platform, subscription).then(() => {
          console.log('Successfully saved subscription');
          this.setState({
            pushNotifications: true,
          });
        });
      });
    } else {
      this.pushService.requestPushUnsubscribe().then((success) => {
        console.log(`Unsubscribe result: ${success}`);
        if (success) {
          this.setState({
            pushNotifications: false,
          });
        }
      });
    }
  }

  saveSubscription = (platform, subscription) => {
    if (platform === 'safari') {
      return this.saveSafariPushSubscription(subscription);
    }

    return this.saveWebpushSubscription(subscription);
  }

  saveSafariPushSubscription = subscription => customFetchV2('/api/v2/safari-push-subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      notificationsEnabled: true,
      data: {
        deviceId: subscription.deviceToken,
      },
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Unable to save safari push subscription, non 2XX response');
      }
    });

  saveWebpushSubscription = subscription => customFetchV2('/api/v2/webpush-subscriptions', {
    method: 'POST',
    body: JSON.stringify({
      notificationsEnabled: true,
      deviceType: 'browser',
      data: subscription,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Unable to save user device subscription, non 2XX response');
      }
    });

  renderContent() {
    if (this.state.isLoading) {
      return (
        <div className="section">
          <Loading />
        </div>
      );
    }

    const pushTooltip = this.state.pushNotificationsDisabled
      ? 'Push notifications aren\'t available for this browser. Try checking your settings to make sure '
      + 'they aren\'t blocked.' : '';
    const smsNotificationsChecked = this.state.smsNotifications
      && this.state.smsNotificationsEligible
      && this.state.smsNotificationsAvailable;
    const smsNotificationsDisabled = !this.state.smsNotificationsEligible
      || !this.state.smsNotificationsAvailable;
    const { pendingConfigUpdates } = this.state;
    const smsConsentLoading = Boolean(pendingConfigUpdates.smsNotifications);
    let smsFooter = null;

    if (this.state.smsConsentExpanded) {
      smsFooter = (
        <SmsConsentPanel
          accepted={this.state.smsConsentAccepted}
          error={this.state.smsConsentError}
          loading={smsConsentLoading}
          onAcceptedChange={this.handleSmsConsentAcceptedChange}
          onCancel={this.cancelSmsConsent}
          onConfirm={this.confirmSmsConsent}
        />
      );
    } else if (smsNotificationsChecked && this.state.smsMonthlyQuota > 0) {
      smsFooter = (
        <SmsNotificationUsage
          used={this.state.smsMonthlyUsed}
          quota={this.state.smsMonthlyQuota}
        />
      );
    }

    return (
      <React.Fragment>
        <Tile
          title="Devices"
          subtitle="How should we notify you about important events (e.g geofence alerts)?">

          {process.env.PLATFORM !== 'mobile' ? (
            <TileActionRow label="Device Push" action={
              <Tooltip title={pushTooltip}>
                <Switch
                  name="pushNotifications"
                  className="up-8"
                  edge="end"
                  onChange={this.handlePushNotificationsChange}
                  checked={this.state.pushNotifications}
                  disabled={this.state.pushNotificationsDisabled}
                  inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
                />
              </Tooltip>} />
          ) : null}
          <TileActionRow label="Email" action={
            <Switch
              className="up-8"
              name="emailNotifications"
              edge="end"
              onChange={this.handleSwitchChange}
              checked={this.state.emailNotifications}
              disabled={Boolean(pendingConfigUpdates.emailNotifications)}
              inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
            />} />

          <TileActionRow
            label="SMS"
            description={
              <SmsNotificationDetails
                available={this.state.smsNotificationsAvailable}
                eligible={this.state.smsNotificationsEligible}
                enabled={smsNotificationsChecked}
              />
            }
            action={
              <Switch
                name="smsNotifications"
                className="up-8"
                edge="end"
                onChange={this.handleSmsNotificationsChange}
                checked={smsNotificationsChecked}
                disabled={smsNotificationsDisabled
                  || smsConsentLoading
                  || this.state.smsConsentExpanded}
                inputProps={{
                  'aria-controls': 'sms-consent-panel',
                  'aria-expanded': this.state.smsConsentExpanded,
                  'aria-label': 'SMS alerts',
                }}
              />
            }
            footer={smsFooter} />
        </Tile>


        <Tile>
          <TileActionRow
            label="Email Receipts"
            description="Automatically send payment receipts to your inbox"
            action={
              <Switch
                name="emailReceipts"
                className="up-8"
                edge="end"
                onChange={this.handleSwitchChange}
                checked={this.state.emailReceipts}
                disabled={Boolean(pendingConfigUpdates.emailReceipts)}
                inputProps={{ 'aria-labelledby': 'switch-list-label-bluetooth' }}
              />
            } />
        </Tile>
      </React.Fragment>
    );
  }

  render() {
    return (
      <>
        {this.renderContent()}
      </>
    );
  }
}
export default NotificationsRoute;
